import type { Env, Health } from "../api";

// Shared pieces: the lab's mark, nav icons, health, places, people.

/** Three stacked shipping containers: the lab's mark. */
export function Mark({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <rect x="2" y="13" width="9" height="7" rx="1" fill="#1b6c80" />
      <rect x="13" y="13" width="9" height="7" rx="1" fill="#1b6c80" opacity="0.55" />
      <rect x="7.5" y="4" width="9" height="7" rx="1" fill="var(--text)" />
    </svg>
  );
}

const paths: Record<string, string> = {
  overview: "M3 13h7V3H3zM14 21h7V11h-7zM3 21h7v-5H3zM14 8h7V3h-7z",
  projects: "M3 7l9-4 9 4-9 4zM3 12l9 4 9-4M3 17l9 4 9-4",
  people: "M16 20v-1a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v1M9.5 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7M21 20v-1a4 4 0 0 0-3-3.9M16 4.1a3.5 3.5 0 0 1 0 6.8",
  audit: "M9 4h6M9 4a2 2 0 0 0-2 2h10a2 2 0 0 0-2-2M7 6H5v15h14V6h-2M9 12h6M9 16h4",
  plus: "M12 5v14M5 12h14",
  costs: "M12 3v18M17 7.5c0-1.9-2.2-3-5-3s-5 1.1-5 3 2.2 2.7 5 3.5 5 1.6 5 3.5-2.2 3-5 3-5-1.1-5-3",
  alert: "M12 3l9.5 16.5H2.5zM12 10v4M12 17.5v.01",
  health: "M3 12h4l3-7 4 14 3-7h4",
  checks: "M4 12.5l4.5 4.5L20 6",
  services: "M4 6h16v4H4zM4 14h16v4H4zM8 8h.01M8 16h.01",
  system: "M3 4h6v5H3zM15 4h6v5h-6zM9 15h6v5H9zM6 9v3h12V9M12 12v3",
  search: "M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14zM21 21l-4.3-4.3",
  menu: "M4 6h16M4 12h16M4 18h16",
  close: "M6 6l12 12M18 6L6 18",
  docs: "M6 3h9l4 4v14H6zM15 3v4h4M9 12h7M9 16h7M9 8h3",
  external: "M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5",
};

export function Icon({ name, size = 16 }: { name: keyof typeof paths; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={paths[name]} />
    </svg>
  );
}

const HEALTH_WORDS: Record<Health, string> = {
  up: "Running",
  "partly-up": "Partly down",
  stopped: "Stopped",
  down: "Down",
  changing: "Changing",
  "not-deployed": "Not deployed",
  unknown: "Unknown",
};

/** The health word, and when it isn't simply running, why — on the page, not only on hover. */
export function HealthMark({ health, reasons }: { health: Health; reasons?: string[] }) {
  const why = health === "up" ? [] : (reasons ?? []);
  return (
    <span style={{ display: "inline-grid", gap: 2 }}>
      <span className={`health ${health}`} title={why.join("\n") || undefined}>
        <span className="dot" />
        {HEALTH_WORDS[health]}
      </span>
      {why.length > 0 && <span className="cell-sub">{why.join(" · ")}</span>}
    </span>
  );
}

/** AWS regions by the city people know them as. */
export const CITY: Record<string, string> = {
  "us-east-1": "Virginia",
  "us-east-2": "Ohio",
  "eu-west-1": "Ireland",
  "eu-central-1": "Frankfurt",
  "me-south-1": "Bahrain",
  "me-central-1": "UAE",
};
export const city = (region: string) => CITY[region] ?? region;

/** What actually runs behind an environment, in one short line. */
export function whatRuns(env: Env): string {
  if (env.onLab) {
    const running = env.containers.filter((c) => c.state === "running").length;
    return `lab host · ${running} of ${env.containers.length} containers running`;
  }
  const parts = [
    ...env.instances.map((i) => (i.type ? `${i.type}` : i.name)),
    ...env.clusters.map((c) => `Fargate · ${c.runningTasks} task${c.runningTasks === 1 ? "" : "s"}`),
    ...env.stacks.map((s) => s.stackName),
  ];
  return parts.join(" + ") || "—";
}

/** A stable pale tint per person, far apart on the wheel for neighbours. */
export function Person({ name, avatarUrl }: { name: string; avatarUrl?: string | null }) {
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) % 360;
  const hue = (h * 137) % 360;
  return (
    <span className="avatar" style={{ background: `hsl(${hue} 45% 90%)`, color: `hsl(${hue} 45% 28%)` }} aria-hidden="true">
      {avatarUrl ? <img src={avatarUrl} alt="" /> : name.slice(0, 1).toUpperCase()}
    </span>
  );
}

export function relative(iso: string | null): string {
  if (!iso) return "—";
  const d = Date.now() - new Date(iso).getTime();
  const m = Math.round(d / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  const days = Math.round(h / 24);
  return days === 1 ? "yesterday" : `${days} days ago`;
}
